import { FormEvent, ReactNode, useId, useState } from "react";
import { Alert, Box, CircularProgress, Link, MenuItem } from "@mui/material";
import BankFactsForm from "./BankFactsForm";
import PpLogo from "../common/PpLogo";
import { textLinkSx } from "../common/textLinkStyles";
import { BuilderCard, BuilderInput, BuilderTextField, CardHeading, PageTitle, PrimaryButton } from "./builderUi";
import { candidateAmount, candidateCard, candidateDate, factsBody, factsErrors, factsFromCandidate } from "../../utils/bankCaseUtils";
import { EvidenceOrderType } from "../../types/evidence";
import { BankCaseOrderBody, BankFactsBody, BankFactsValues, OrderCandidate } from "../../types/evidenceBankCase";

export interface NewBankCase extends BankFactsBody, BankCaseOrderBody {
  arn?: string;
}

const ORDER_TYPE_LABELS: Record<EvidenceOrderType, string> = { initial: "First order", renewal: "Renewal" };

function OrderRow({ label, labelId, children }: { label: string; labelId?: string; children: ReactNode }) {
  return (
    <>
      <Box id={labelId} sx={{ color: "#667085" }}>{label}</Box>
      <Box sx={{ fontWeight: 600, wordBreak: "break-word", minWidth: 0 }}>{children}</Box>
    </>
  );
}

export function OrderSummary({ candidate, companyName, children }: {
  candidate: OrderCandidate;
  companyName: string;
  /** Further rows, laid out on the same grid. */
  children?: ReactNode;
}) {
  const card = candidateCard(candidate);
  const paid = candidateAmount(candidate.amount, candidate.currency);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "86px 1fr", rowGap: "10px", columnGap: "12px", alignItems: "center", fontSize: 13, color: "#1d2939" }}>
      <OrderRow label="Order">
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}><PpLogo ppType={candidate.source} size={18} /><span>{candidate.orderName}</span></Box>
      </OrderRow>
      <OrderRow label="Company">{companyName}</OrderRow>
      {candidate.customerEmail && <OrderRow label="Customer">{candidate.customerEmail}</OrderRow>}
      {candidate.placedOn && <OrderRow label={candidate.matchedOn === "order_number" ? "Placed" : "Paid"}>{candidateDate(candidate.placedOn)}</OrderRow>}
      {(paid || card) && <OrderRow label={candidate.matchedOn === "order_number" ? "Total" : "Payment"}>{[paid, card].filter(Boolean).join(" · ")}</OrderRow>}
      {children}
    </Box>
  );
}

/** The order type row of OrderSummary: a known type shows as a value the agent can change, an unknown one asks at once. */
export function OrderTypeRow({ value, known = false, tried, onChange }: {
  value: EvidenceOrderType | "";
  /** The search told the type, so it shows as a value until the agent asks to change it. */
  known?: boolean;
  /** The agent tried to go on, so an empty field says why they cannot. */
  tried: boolean;
  onChange: (orderType: EvidenceOrderType) => void;
}) {
  const labelId = useId();
  const [changing, setChanging] = useState(!known);
  const error = tried && !value ? "Pick first order or renewal" : "";
  return (
    <OrderRow label="Order type" labelId={labelId}>
      {changing || !value ? (
        <BuilderInput
          select
          value={value}
          onChange={(event) => onChange(event.target.value as EvidenceOrderType)}
          error={Boolean(error)}
          helperText={error || undefined}
          slotProps={{ select: { labelId } }}
        >
          {(Object.keys(ORDER_TYPE_LABELS) as EvidenceOrderType[]).map((type) => <MenuItem key={type} value={type}>{ORDER_TYPE_LABELS[type]}</MenuItem>)}
        </BuilderInput>
      ) : (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
          <span>{ORDER_TYPE_LABELS[value]}</span>
          <Link component="button" type="button" underline="none" onClick={() => setChanging(true)} sx={textLinkSx}>Change</Link>
        </Box>
      )}
    </OrderRow>
  );
}

export default function BankCaseDetails({ candidate, companyName, submitting, error, onBack, onSubmit }: {
  candidate: OrderCandidate;
  companyName: string;
  submitting: boolean;
  /** Why the last attempt created nothing. */
  error: string | null;
  onBack: () => void;
  onSubmit: (bankCase: NewBankCase) => void;
}) {
  const [values, setValues] = useState<BankFactsValues>(() => factsFromCandidate(candidate));
  const [orderType, setOrderType] = useState<EvidenceOrderType | "">(candidate.orderType ?? "");
  const [arn, setArn] = useState("");
  const [tried, setTried] = useState(false);

  const errors = factsErrors(values);
  const arnError = /[^A-Za-z0-9_]/.test(arn.trim()) ? "An ARN is letters, digits and underscores only" : "";

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (Object.keys(errors).length > 0 || arnError || !orderType) return;
    onSubmit({
      order: { source: candidate.source, accountId: candidate.accountId, orderKey: candidate.orderKey, orderName: candidate.orderName },
      orderType,
      ...(arn.trim() ? { arn: arn.trim() } : {}),
      ...factsBody(values),
    });
  };

  return (
    <Box component="form" onSubmit={submit} noValidate sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
        <Link component="button" type="button" underline="none" onClick={onBack} sx={{ fontSize: 12 }}>Back to the order search</Link>
        <PageTitle>Dispute details</PageTitle>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: "20px", alignItems: { xs: "stretch", md: "flex-start" } }}>
        <BuilderCard component="section" aria-label="Order" sx={{ width: { md: 380 }, flexShrink: 0, gap: "12px" }}>
          <CardHeading>Order</CardHeading>
          <OrderSummary candidate={candidate} companyName={companyName}>
            <OrderTypeRow value={orderType} known={Boolean(candidate.orderType)} tried={tried} onChange={setOrderType} />
          </OrderSummary>
        </BuilderCard>

        <BuilderCard sx={{ flexGrow: 1, minWidth: 0, gap: "14px" }}>
          <CardHeading>From the bank&apos;s portal</CardHeading>
          <BankFactsForm values={values} errors={tried ? errors : {}} disabled={submitting} onChange={setValues}>
            <BuilderTextField
              label="ARN (optional)"
              placeholder="Acquirer reference number"
              value={arn}
              onChange={(event) => setArn(event.target.value)}
              error={tried && Boolean(arnError)}
              helperText={tried ? arnError : undefined}
              disabled={submitting}
            />
          </BankFactsForm>
          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: "4px" }}>
            <PrimaryButton type="submit" disabled={submitting} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
              Generate evidence
            </PrimaryButton>
          </Box>
        </BuilderCard>
      </Box>
    </Box>
  );
}
